import { Check, Copy } from "lucide-react";
import { useId, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { m } from "@/paraglide/messages";
import { binaryToText, textToBinary } from "../lib/binary";

type Mode = "encode" | "decode";
const SAMPLE = "Hi";

export function TextToBinaryTool() {
  const uid = useId();
  const [mode, setMode] = useState<Mode>("encode");
  const [input, setInput] = useState(SAMPLE);
  const [copied, setCopied] = useState(false);

  const result = useMemo(() => {
    if (mode === "encode") {
      return { ok: true as const, text: textToBinary(input) };
    }
    return binaryToText(input);
  }, [mode, input]);

  const copy = async () => {
    if (!result.ok || !result.text) return;
    try {
      await navigator.clipboard.writeText(result.text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // clipboard unavailable — ignore
    }
  };

  return (
    <div className="space-y-6">
      <div className="inline-flex rounded-lg border border-border bg-card/60 p-1">
        <button
          type="button"
          aria-pressed={mode === "encode"}
          onClick={() => setMode("encode")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "encode"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.tb_mode_encode()}
        </button>
        <button
          type="button"
          aria-pressed={mode === "decode"}
          onClick={() => setMode("decode")}
          className={`rounded-md px-4 py-1.5 text-sm font-medium transition-colors ${
            mode === "decode"
              ? "bg-primary text-primary-foreground"
              : "text-muted-foreground hover:text-foreground"
          }`}
        >
          {m.tb_mode_decode()}
        </button>
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={`${uid}-in`}>{m.tb_input_label()}</Label>
        <textarea
          id={`${uid}-in`}
          value={input}
          onChange={(e) => setInput(e.target.value)}
          placeholder={
            mode === "encode"
              ? m.tb_placeholder_encode()
              : m.tb_placeholder_decode()
          }
          spellCheck={false}
          className="min-h-[8rem] w-full resize-y rounded-lg border border-border bg-background p-4 font-mono text-sm"
        />
      </div>

      <div className="space-y-3 rounded-2xl border border-border bg-card/60 p-6 sm:p-8">
        <div className="flex items-center justify-between">
          <Label htmlFor={`${uid}-out`}>{m.tb_output_label()}</Label>
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={copy}
            disabled={!result.ok || !result.text}
            className="gap-2"
          >
            {copied ? (
              <Check className="h-4 w-4 text-emerald-500" />
            ) : (
              <Copy className="h-4 w-4" />
            )}
            {copied ? m.tb_copied() : m.tb_copy()}
          </Button>
        </div>
        {result.ok ? (
          <textarea
            id={`${uid}-out`}
            readOnly
            data-testid="tb-output"
            value={result.text}
            spellCheck={false}
            className="min-h-[8rem] w-full resize-y break-all rounded-lg border border-border bg-background p-4 font-mono text-sm"
          />
        ) : (
          <p
            id={`${uid}-out`}
            data-testid="tb-error"
            className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-300"
          >
            {m.tb_invalid()}
          </p>
        )}
      </div>
    </div>
  );
}
